import { deserialize, DocumentObject } from '@jsonapiworld/jsonapi-serializer';
import { addDays, endOfYear, parseISO, startOfYear } from 'date-fns';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import { backendApiGateway } from '~/client/backend';
import { CategoryFilterValue } from '~/types/category';
import { Company } from '~/types/company';
import { CostCenterFilterValue } from '~/types/cost-center';
import {
  Document,
  DocumentCreator,
  DocumentDirection,
  DocumentPaymentStatus,
  DocumentsMeta,
  DocumentsQuerySorting,
  DocumentStatus,
  DocumentType,
  InvoiceImportConfig,
  InvoiceImportPayload,
  InvoiceImportTemplate,
} from '~/types/document';
import { FlowPaymentStatus, PaymentMethod } from '~/types/flow';
import { TransactionFlowDirection } from '~/types/transaction';
import { isDefined } from '~/types/typeGuards';
import { BillTypeFilter } from '~/types/vat-bookkeeping';
import { addCategoryFilter } from '~/utils/filters';
import { formatDateForAPI, formatISOForAPI } from '~/utils/formatDate';
import {
  buildJsonAPIQueryParams,
  IJsonAPIFilterBuilder,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFieldsBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
  SortInfo,
} from '~/utils/jsonapi-query';

export interface DocumentsQueryFilters {
  totalAmount?: [number | null, number | null, (TransactionFlowDirection | null)?] | null;
  creationDate?: [string | null, string | null];
  documentTypes?: DocumentType[];
  currencies?: string[];
  paymentStatus?: DocumentPaymentStatus | null;
  direction?: DocumentDirection | null;
  search?: string;
  searchBills?: string;
  category?: CategoryFilterValue | null;
  costCenter?: CostCenterFilterValue;
  withNumber?: boolean;
  number?: string;
  numberIcase?: string;
  status?: DocumentStatus[];
  counterpart?: string[];
  counterpartCountry?: string;
  ids?: Document['id'][];
  isElectronic?: boolean;
  excludeIds?: Document['id'][];
  flowCount?: {
    operator: '=' | '<' | '<=' | '>=' | '>';
    value: number;
  };
  flowHasPaymentMethod?: PaymentMethod[];
  flowHasPaymentStatus?: FlowPaymentStatus | null;
  flowHasExpectedPaymentDate?: [string | null, string | null];
  includeHidden?: boolean;
  hasVATRegistrations?: boolean;
  detectionDatetime?: [string | null, string | null];
  hasBookkeepingTransactions?: boolean;
  hasOpenBalance?: boolean;
  hasWithholdingFlow?: boolean;
  nonWithholdingFlowPaymentDate?: [string | null, string | null];
  withholdingFlowPaymentDate?: [string | null, string | null];
  withholdingFlowPaymentStatus?: FlowPaymentStatus | null;
  deviceIds?: string[];
  billType?: BillTypeFilter | null;
}

type DocumentsIncludeOptions =
  | 'attachments'
  | 'allocations'
  | 'allocations.category'
  | 'allocations.subcategory'
  | 'allocations.costCenter'
  | 'bookkeepingEntries'
  | 'category'
  | 'company'
  | 'company.companyIdentity'
  | 'counterpart'
  | 'flows'
  | 'flows.account'
  | 'flows.account.consent'
  | 'flows.account.consent.institution'
  | 'flows.bookkeepingEntry'
  | 'flows.payments'
  | 'subcategory'
  | 'related'
  | 'referencedBy'
  | 'vatRegistrations';

export const DocumentsPageSize = 50;

const defaultSorting: SortInfo<DocumentsQuerySorting> = {
  sort: 'searchDate',
  direction: 'desc',
};

const documentsFiltersToJsonApiFilters = (filters: DocumentsQueryFilters): IJsonAPIFilterBuilder => {
  const builder = jsonAPIFilterBuilder();
  if (!isNil(filters.totalAmount)) {
    if (!isNil(filters.totalAmount[0])) builder.gte('grossAmountAmount', filters.totalAmount[0]);
    if (!isNil(filters.totalAmount[1])) builder.lte('grossAmountAmount', filters.totalAmount[1]);
    if (!isNil(filters.totalAmount[2])) {
      const isInFlow = filters.totalAmount[2] === TransactionFlowDirection.Inflow ? true : false;
      builder.eq('isInflow', isInFlow);
    }
  }
  if (!isNil(filters.creationDate?.[0])) {
    const creationDate = formatISOForAPI(filters.creationDate[0]);
    builder.gte('creationDate', creationDate);
  }
  if (!isNil(filters.creationDate?.[1])) {
    const creationDate = formatDateForAPI(addDays(parseISO(filters.creationDate[1]), 1));
    builder.lt('creationDate', creationDate);
  }

  if (!isNil(filters.detectionDatetime?.[0])) {
    builder.gte('detectionDatetime', filters.detectionDatetime[0]);
  }
  if (!isNil(filters.detectionDatetime?.[1])) {
    builder.lt('detectionDatetime', filters.detectionDatetime[1]);
  }

  if (!isNil(filters.direction)) builder.eq('documentDirection', filters.direction);
  if (!isNil(filters.paymentStatus)) builder.eq('paymentStatus', filters.paymentStatus);
  if (filters.documentTypes?.length) builder.in('documentType', filters.documentTypes);
  if (filters.currencies?.length) builder.in('grossAmountCurrency', filters.currencies);

  if (filters.search) builder.ilike('search', filters.search);
  if (filters.searchBills) builder.ilike('searchBills', filters.searchBills);

  if (filters.category) addCategoryFilter(builder, filters.category);

  if (filters.costCenter === null) {
    builder.empty('cost_centers_id', true);
  } else if (filters.costCenter && filters.costCenter.length >= 1) {
    builder.in('cost_centers_id', filters.costCenter);
  }

  if (filters.withNumber) builder.empty('number', false);
  if (filters.number) {
    builder.eq('number', filters.number);
  }
  if (filters.numberIcase) builder.eq('numberIcase', filters.numberIcase);

  if (filters.status?.length) builder.in('status', filters.status);
  if (filters.counterpart?.length) builder.in('counterpart.id', filters.counterpart);
  if (filters.counterpartCountry) builder.eq('counterpart.country', filters.counterpartCountry);

  if (filters.ids) {
    builder.in('id', filters.ids);
  }
  if (filters.excludeIds && filters.excludeIds.length > 0) {
    builder.notIn('id', filters.excludeIds);
  }

  if (isDefined(filters.flowCount)) {
    const { operator, value } = filters.flowCount;
    switch (operator) {
      case '=':
        builder.eq('flowCount', value);
        break;
      case '<':
        builder.lt('flowCount', value);
        break;
      case '<=':
        builder.lte('flowCount', value);
        break;
      case '>=':
        builder.gte('flowCount', value);
        break;
      case '>':
        builder.gt('flowCount', value);
        break;
    }
  }

  if (isDefined(filters.flowHasPaymentMethod) && filters.flowHasPaymentMethod.length > 0) {
    builder.in('flowHasPaymentMethod', filters.flowHasPaymentMethod);
  }
  if (isDefined(filters.flowHasPaymentStatus)) {
    builder.eq('flowHasPaymentStatus', filters.flowHasPaymentStatus);
  }
  if (isDefined(filters.flowHasExpectedPaymentDate?.[0])) {
    const creationDate = formatISOForAPI(filters.flowHasExpectedPaymentDate[0]);
    builder.gte('flowHasExpectedPaymentDate', creationDate);
  }
  if (isDefined(filters.flowHasExpectedPaymentDate?.[1])) {
    const creationDate = formatDateForAPI(addDays(parseISO(filters.flowHasExpectedPaymentDate[1]), 1));
    builder.lt('flowHasExpectedPaymentDate', creationDate);
  }

  if (isDefined(filters.hasVATRegistrations)) {
    builder.eq('hasVatRegistrations', filters.hasVATRegistrations ? 'true' : 'false');
  }
  if (isDefined(filters.hasBookkeepingTransactions)) {
    builder.eq('hasBookkeepingTransactions', filters.hasBookkeepingTransactions ? 'true' : 'false');
  }

  if (isDefined(filters.isElectronic)) {
    builder.eq('is_e_invoice', filters.isElectronic ? 'true' : 'false');
  }

  if (filters.includeHidden === false) {
    builder.empty('hiddenAt', true);
  }

  if (isDefined(filters.hasOpenBalance)) {
    builder.eq('hasOpenBalance', filters.hasOpenBalance ? 'true' : 'false');
  }

  if (isDefined(filters.hasWithholdingFlow)) {
    builder.eq('has_withholding_flow', filters.hasWithholdingFlow ? 'true' : 'false');
  }

  if (isDefined(filters.nonWithholdingFlowPaymentDate?.[0])) {
    const date = formatISOForAPI(filters.nonWithholdingFlowPaymentDate[0]);
    builder.gte('non_withholding_flow_payment_date', date);
  }
  if (isDefined(filters.nonWithholdingFlowPaymentDate?.[1])) {
    const date = formatDateForAPI(addDays(parseISO(filters.nonWithholdingFlowPaymentDate[1]), 1));
    builder.lt('non_withholding_flow_payment_date', date);
  }

  if (isDefined(filters.withholdingFlowPaymentDate?.[0])) {
    const date = formatISOForAPI(filters.withholdingFlowPaymentDate[0]);
    builder.gte('withholding_flow_payment_date', date);
  }
  if (isDefined(filters.withholdingFlowPaymentDate?.[1])) {
    const date = formatDateForAPI(addDays(parseISO(filters.withholdingFlowPaymentDate[1]), 1));
    builder.lt('withholding_flow_payment_date', date);
  }

  if (isDefined(filters.withholdingFlowPaymentStatus)) {
    builder.eq('withholding_flow_payment_status', filters.withholdingFlowPaymentStatus);
  }

  if (isDefined(filters.deviceIds) && filters.deviceIds.length > 0) {
    builder.in('deviceId', filters.deviceIds);
  }
  if (isDefined(filters.billType)) {
    builder.empty('deviceId', filters.billType === BillTypeFilter.Other);
  }

  return builder;
};

const createSortingBuilder = (sorting: SortInfo<DocumentsQuerySorting>) => {
  const sort = uniqBy(
    [
      {
        sort: sorting.sort,
        direction: sorting.direction,
      },
      {
        sort: 'creationDate',
        direction: sorting.direction,
      },
      {
        sort: 'createdAt',
        direction: sorting.direction,
      },
      {
        sort: 'id',
        direction: sorting.direction,
      },
    ],
    (x) => x.sort,
  );
  return jsonAPISortBuilder(sort);
};

const defaultInclude: DocumentsIncludeOptions[] = [
  'flows',
  'flows.bookkeepingEntry',
  'category',
  'subcategory',
  'counterpart',
];

interface FetchDocumentsOptions {
  size?: number;
  cursor?: string;
  sorting?: SortInfo<DocumentsQuerySorting>;
  include?: DocumentsIncludeOptions[];
}

export const fetchDocuments = async (
  companyId: Company['id'],
  filters: DocumentsQueryFilters,
  options: FetchDocumentsOptions,
) => {
  const includeBuilder = jsonAPIIncludeBuilder(options.include ?? defaultInclude);

  const filterBuilder = documentsFiltersToJsonApiFilters(filters);
  filterBuilder.eq('company.id', companyId);

  const sortingBuilder = createSortingBuilder(options.sorting ?? defaultSorting);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(options.size ?? DocumentsPageSize);
  if (options.cursor) paginationParamsBuilder.cursor(options.cursor);

  const searchParams = buildJsonAPIQueryParams(filterBuilder, includeBuilder, sortingBuilder, paginationParamsBuilder);
  const { data } = await backendApiGateway.get<DocumentObject & { meta: DocumentsMeta }>('/api/v1/documents', {
    params: searchParams,
  });

  return {
    documents: deserialize<Document>(data) as Document[],
    page: (data.meta as DocumentsMeta).page,
  };
};

export const fetchLastProgressiveBySectional = async (
  companyId: Company['id'],
  documentType: DocumentType,
  direction: DocumentDirection,
  creators: DocumentCreator[] | undefined,
  sectionalId: string | null,
  creationDate: string,
): Promise<bigint | null> => {
  const parsedDate = parseISO(creationDate);
  const from = formatDateForAPI(startOfYear(parsedDate));
  const to = formatDateForAPI(endOfYear(parsedDate));

  const filterBuilder = documentsFiltersToJsonApiFilters({
    currencies: [],
    creationDate: [from, to],
    paymentStatus: null,
    search: '',
    totalAmount: null,
    withNumber: true,
    documentTypes: [documentType],
  });
  filterBuilder.eq('company.id', companyId);
  filterBuilder.eq('direction', direction);
  if (creators?.length) filterBuilder.in('creator', creators);
  filterBuilder.empty('progressive', false);
  if (sectionalId) {
    filterBuilder.eq('sectional.id', sectionalId);
  } else {
    filterBuilder.empty('sectional.id', true);
  }
  filterBuilder.neq('status', DocumentStatus.Draft);

  const sortingBuilder = jsonAPISortBuilder([{ sort: 'progressive', direction: 'desc' }]);
  const fieldsBuilder = jsonAPIFieldsBuilder([{ resource: 'document', fields: ['progressive'] }]);
  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(1);

  const searchParams = buildJsonAPIQueryParams(filterBuilder, fieldsBuilder, sortingBuilder, paginationParamsBuilder);
  const { data } = await backendApiGateway.get<DocumentObject & { meta: DocumentsMeta }>('/api/v1/documents', {
    params: searchParams,
  });

  const documentList = deserialize<Document>(data) as Document[];
  return documentList[0]?.progressive ? BigInt(documentList[0].progressive) : null;
};

export const fetchDocumentImportTemplates = async ({ companyId }: { companyId: Company['id'] }) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  const searchParams = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<{ templates: InvoiceImportTemplate[] }>(
    `/api/v1/documents/import-templates`,
    { params: searchParams },
  );
  return data.templates;
};

export const fetchDocumentImportConfig = async ({
  companyId,
  templateId,
  documentType,
}: {
  companyId: Company['id'];
  templateId: string;
  documentType?: DocumentType;
}) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  const searchParams = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<{ config: InvoiceImportConfig }>(`/api/v1/documents/import-config`, {
    params: { ...searchParams, template: templateId, ...(documentType ? { document_type: documentType } : {}) },
  });
  return data.config;
};

export const importInvoices = async ({
  companyId,
  payload,
}: {
  companyId: Company['id'];
  payload: InvoiceImportPayload;
}) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  const searchParams = buildJsonAPIQueryParams(filterBuilder);

  await backendApiGateway.post(`/api/v1/documents/import-invoices`, payload, {
    params: searchParams,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
};

export const fetchPayrollDownloadUrl = async (id: Document['id']): Promise<string> => {
  const { data } = await backendApiGateway.get<{ url: string }>(`/api/v1/payroll/${id}/download-url`);
  return data.url;
};
